<script setup lang="ts">
import type { Component } from 'vue'
import type { BannerClickPayload } from './banner.types'
import IconChevronLeft from '~icons/material-symbols/chevron-left'
import IconChevronRight from '~icons/material-symbols/chevron-right'

export interface ImageBanner {
  name: string
  imageXs: string
  imageSm: string
  imageMd: string
  imageLg: string
  link?: string
  alt?: string
}

export interface ComponentBanner {
  name: string
  url?: string
  component: Component
  componentProps?: Record<string, unknown>
}

export type Banner = ImageBanner | ComponentBanner

const props = withDefaults(defineProps<Props>(), {
  banners: () => [],
  autoPlayInterval: 5000,
})

const emit = defineEmits<{
  bannerClick: [payload: BannerClickPayload]
}>()

function isComponentBanner(banner: Banner): banner is ComponentBanner {
  return 'component' in banner
}

interface Props {
  banners?: Banner[]
  autoPlayInterval?: number
}

const currentIndex = ref(0)
const slideDirection = ref<'next' | 'prev'>('next')
const containerWidth = ref(0)
const containerRef = ref<HTMLElement | null>(null)
let autoPlayTimer: ReturnType<typeof setInterval> | null = null
let resizeObserver: ResizeObserver | null = null

const hasBanners = computed(() => props.banners.length > 0)
const currentBanner = computed(() => props.banners[currentIndex.value])

const imageBreakpoints = [
  { minWidth: 1024, key: 'imageLg' as const },
  { minWidth: 768, key: 'imageMd' as const },
  { minWidth: 360, key: 'imageSm' as const },
  { minWidth: 0, key: 'imageXs' as const },
] as const

const currentBannerImage = computed(() => {
  const banner = currentBanner.value
  if (!banner || isComponentBanner(banner))
    return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="1" height="1"%3E%3Crect width="100%25" height="100%25" fill="%23000"%3E%3C/svg%3E'

  const width = containerWidth.value
  const breakpoint = imageBreakpoints.find(bp => width >= bp.minWidth)
  return breakpoint ? banner[breakpoint.key] : banner.imageXs
})

const currentBannerAlt = computed(() => {
  const banner = currentBanner.value
  return (banner && !isComponentBanner(banner) ? banner.alt : undefined) || 'Banner'
})

function nextBanner() {
  if (props.banners.length === 0)
    return
  slideDirection.value = 'next'
  currentIndex.value = (currentIndex.value + 1) % props.banners.length
  resetAutoPlay()
}

function previousBanner() {
  if (props.banners.length === 0)
    return
  slideDirection.value = 'prev'
  currentIndex.value = currentIndex.value === 0 ? props.banners.length - 1 : currentIndex.value - 1
  resetAutoPlay()
}

function handleBannerClick() {
  const banner = currentBanner.value
  if (!banner)
    return

  emit('bannerClick', {
    name: banner.name,
    url: isComponentBanner(banner) ? banner.url : banner.link,
  })

  if (isComponentBanner(banner))
    return

  if (banner.link)
    window.open(banner.link, '_blank')
}

function startAutoPlay() {
  if (props.banners.length <= 1)
    return

  stopAutoPlay()
  autoPlayTimer = setInterval(() => {
    nextBanner()
  }, props.autoPlayInterval)
}

function stopAutoPlay() {
  if (autoPlayTimer) {
    clearInterval(autoPlayTimer)
    autoPlayTimer = null
  }
}

function resetAutoPlay() {
  stopAutoPlay()
  if (props.banners.length > 1) {
    startAutoPlay()
  }
}

function handleMouseLeave() {
  if (props.banners.length > 1) {
    startAutoPlay()
  }
}

watch(() => props.banners, () => {
  if (props.banners.length > 0 && currentIndex.value >= props.banners.length) {
    currentIndex.value = 0
  }
  resetAutoPlay()
}, { immediate: true })

onMounted(() => {
  startAutoPlay()
  setupResizeObserver()
})

onUnmounted(() => {
  stopAutoPlay()
  cleanupResizeObserver()
})

function setupResizeObserver() {
  if (typeof window === 'undefined' || !containerRef.value)
    return

  resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      containerWidth.value = entry.contentRect.width
    }
  })

  resizeObserver.observe(containerRef.value)
}

function cleanupResizeObserver() {
  if (resizeObserver) {
    resizeObserver.disconnect()
    resizeObserver = null
  }
}
</script>

<template>
  <div
    v-if="hasBanners"
    ref="containerRef"
    class="banner-carousel"
    @mouseenter="stopAutoPlay"
    @mouseleave="handleMouseLeave"
  >
    <div class="banner-carousel-container">
      <button
        v-if="banners.length > 1"
        class="banner-carousel-button banner-carousel-button-prev"
        @click="previousBanner"
      >
        <IconChevronLeft class="banner-carousel-button-icon" />
      </button>

      <div
        v-if="banners.length > 1"
        class="banner-carousel-slides-wrapper"
      >
        <Transition
          :name="slideDirection === 'next' ? 'banner-item' : 'banner-item-reverse'"
        >
          <div
            :key="currentIndex"
            class="banner-carousel-slide"
            @click="handleBannerClick"
          >
            <component
              :is="currentBanner.component"
              v-if="currentBanner && isComponentBanner(currentBanner)"
              v-bind="currentBanner.componentProps"
              class="banner-carousel-image"
            />
            <img
              v-else
              :src="currentBannerImage"
              :alt="currentBannerAlt"
              class="banner-carousel-image"
            >
          </div>
        </Transition>
      </div>

      <div
        v-else
        class="banner-carousel-slide"
        @click="handleBannerClick"
      >
        <component
          :is="currentBanner.component"
          v-if="currentBanner && isComponentBanner(currentBanner)"
          v-bind="currentBanner.componentProps"
          class="banner-carousel-image"
        />
        <img
          v-else
          :src="currentBannerImage"
          :alt="currentBannerAlt"
          class="banner-carousel-image"
        >
      </div>

      <button
        v-if="banners.length > 1"
        class="banner-carousel-button banner-carousel-button-next"
        @click="nextBanner"
      >
        <IconChevronRight class="banner-carousel-button-icon" />
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.banner-carousel {
  @apply flex flex-col gap-4 w-full ;
  @apply self-stretch;

  @screen sm:system {
    @apply w-[310px];
    flex: 0 0 310px;
  }

  &-container {
    @apply relative w-full rounded-lg flex-1 min-h-[200px];
    @apply bg-neutral-high-light overflow-hidden;

    @screen sm:system {
      @apply bg-brand-primary-light min-h-[300px] flex;
    }
  }

  &-slides-wrapper {
    @apply absolute inset-0 w-full overflow-hidden;
    z-index: 1;
  }

  &-slide {
    @apply w-full h-full cursor-pointer absolute top-0 left-0 right-0 bottom-0;
    @apply rounded-lg overflow-hidden;
    z-index: 1;

    @screen sm:system {
      @apply bg-neutral-low-pure m-0;
    }
  }

  &-image {
    @apply w-full h-full object-cover object-center;
  }

  &-button {
    @apply absolute top-1/2 -translate-y-1/2;
    @apply w-12 h-12 rounded-full z-10;
    @apply bg-neutral-high-pure;
    @apply flex items-center justify-center;
    @apply transition-all duration-200;
    @apply active:scale-95;
    @apply border border-neutral-high-dark;
    @apply cursor-pointer;
    @apply backdrop-blur-sm;

    &-prev {
      @apply left-2;

      @screen md:system {
        @apply left-4;
      }

      @screen sm:system {
        left: 3.78%;
        @apply bg-neutral-high-medium bg-opacity-64 border-0;
      }
    }

    &-next {
      @apply right-2;

      @screen md:system {
        @apply right-4;
      }

      @screen sm:system {
        right: 3.78%;
        @apply bg-neutral-high-medium bg-opacity-64 border-0;
      }
    }

    &-icon {
      @apply w-7 h-7 flex-shrink-0;
      @apply text-neutral-low-dark;
      @apply transition-transform duration-200;
    }

    &:hover &-icon {
      @apply scale-110;
    }

    &:disabled {
      @apply opacity-50 cursor-not-allowed;
      @apply hover:bg-neutral-high-pure;
    }

    &:not(:disabled) {
      @apply pointer-events-auto visible;
    }

    @media (hover: hover) and (pointer: fine) {
      @apply opacity-0 pointer-events-none transition-opacity duration-200;
    }
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover &-button:not(:disabled),
    &:focus-within &-button:not(:disabled) {
      @apply opacity-100 pointer-events-auto;
    }
  }
}

.banner-item-enter-active,
.banner-item-leave-active {
  transition: all 0.4s ease-in-out;
}

.banner-item-leave-active {
  @apply absolute inset-0 w-full h-full z-1;
}

.banner-item-enter-active {
  @apply absolute inset-0 w-full h-full z-2;
}

.banner-item-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.banner-item-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

.banner-item-enter-to,
.banner-item-leave-from {
  transform: translateX(0);
  opacity: 1;
}

.banner-item-reverse-enter-active,
.banner-item-reverse-leave-active {
  transition: all 0.4s ease-in-out;
}

.banner-item-reverse-leave-active {
  @apply absolute inset-0 w-full h-full z-1;
}

.banner-item-reverse-enter-active {
  @apply absolute inset-0 w-full h-full z-2;
}

.banner-item-reverse-enter-from {
  transform: translateX(-100%);
  opacity: 0;
}

.banner-item-reverse-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.banner-item-reverse-enter-to,
.banner-item-reverse-leave-from {
  transform: translateX(0);
  opacity: 1;
}
</style>
